import { lazy, Suspense } from 'react';

import SpinnerBasic from 'components/spinners/SpinnerBasic';

/**
 * Creates a lazy-loaded React component with manual preload support.
 *
 * This helper extends React.lazy by attaching a `preloadFn()` method
 * to the returned component. Calling `preloadFn()` starts downloading
 * the component bundle ahead of time (for example, on hover or focus),
 * without rendering the component.
 *
 * If the preload fails, the internal promise is reset so the next call
 * can retry the download.
 *
 * Important:
 *  - `preloadFn()` only preloads the module; it does NOT render it
 *  - the component must still be rendered inside <Suspense>
 *
 * @param {Function} importFn
 *        Function that dynamically imports a component
 *        Example: () => import('./MyComponent')
 *
 * @returns {Object}
 *          A React.lazy component with an attached `preloadFn()` method
 *
 * @example
 * const MyLazyComponent = lazyWithPreload(() => import('./MyComponent'));
 *
 * <button onMouseEnter={() => MyLazyComponent.preloadFn()}>
 *   Open
 * </button>
 */
export const lazyWithPreload = (importFn) => {
  const Component = lazy(importFn);

  let preloadPromise = null;

  Component.preloadFn = () => {
    // reset the promise if loading fails, so it can retry next time
    if (!preloadPromise) {
      preloadPromise = importFn().catch(() => (preloadPromise = null));
    }
    return preloadPromise;
  };

  return Component;
};

/**
 * Wraps a lazy-loaded component with React.Suspense.
 *
 * This utility avoids repeating Suspense boilerplate and provides
 * a default loading spinner while the lazy component is being fetched.
 *
 * @param {Object} LazyComponent
 *        Component created using React.lazy or lazyWithPreload
 *
 * @param {*} [spinner=<SpinnerBasic />]
 *        Optional fallback shown while loading
 *
 * @returns {JSX.Element}
 *          The lazy component wrapped in <Suspense>
 *
 * @example
 * const Page = lazyWithPreload(() => import('./Page'));
 *
 * {getLazyComponentWithSuspense(Page)}
 */
export const getLazyComponentWithSuspense = (LazyComponent, spinner = <SpinnerBasic />) => (
  <Suspense fallback={spinner}>
    <LazyComponent />
  </Suspense>
);
